import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  ProgressBar,
  Button,
  Alert,
  Badge,
  Table,
} from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FaChartLine,
  FaCrown,
  FaKey,
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaArrowUp,
  FaGem,
  FaHistory,
  FaSync,
  FaBook,
  FaCopy,
} from "react-icons/fa";
import { getDashboardInfo, useUser, useAppDispatch } from "../../redux";
import {
  getLatestApiLogs,
  getApiUsageAnalytics,
  selectApiLogs,
  selectApiAnalytics,
  selectApiLogsLoading,
} from "../../redux/slices/apiLogsSlice";
import { useSelector } from "react-redux";
import Spinner from "../common/Spinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const HomeTab = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, dashboardInfo } = useUser();
  const apiLogs = useSelector(selectApiLogs);
  const apiAnalytics = useSelector(selectApiAnalytics);
  const apiLogsLoading = useSelector(selectApiLogsLoading);

  useEffect(() => {
    dispatch(getDashboardInfo());
    dispatch(getLatestApiLogs());
    dispatch(getApiUsageAnalytics());
  }, [dispatch]);

  // Simple function to format the timestamp from database
  // eslint-disable-next-line no-unused-vars
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";

    const date = new Date(timestamp);
    return date.toLocaleString(); // Shows date and time in user's locale
  };

  // Alternative: Just show time if you prefer
  const formatTime = (timestamp) => {
    if (!timestamp) return "Unknown";

    const date = new Date(timestamp);
    // This will automatically convert UTC to local timezone
    return date.toLocaleTimeString();
  };

  // Prepare chart data from real analytics
  const chartData = {
    labels:
      apiAnalytics?.map((item) => {
        const date = new Date(item?.date);
        return date.getDate().toString();
      }) || [],
    datasets: [
      {
        label: "API Requests",
        data: apiAnalytics?.map((item) => item?.apiCalls || 0) || [],
        borderColor: "#0d6efd",
        backgroundColor: "rgba(13, 110, 253, 0.1)",
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: "#0d6efd",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        fill: true,
        borderWidth: 3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#0d6efd",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function (context) {
            const dataIndex = context[0]?.dataIndex;
            const analyticsItem = apiAnalytics?.[dataIndex];
            return `${analyticsItem?.day || `Day ${context[0]?.label}`}`;
          },
          label: function (context) {
            return `API Requests: ${context.parsed.y.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: "#6c757d",
          font: { size: 12 },
          callback: function (value) {
            return value >= 1000 ? value / 1000 + "K" : value;
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
          borderDash: [5, 5],
        },
        border: { display: false },
      },
      x: {
        ticks: {
          color: "#6c757d",
          font: { size: 12 },
          maxTicksLimit: 10,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        border: { display: false },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  if (loading && !dashboardInfo) {
    return (
      <div className="text-center my-5">
        <Spinner />
        <p className="mt-3 text-muted">Loading your dashboard...</p>
      </div>
    );
  }

  if (!dashboardInfo) {
    return (
      <div className="text-center my-5">
        <FaExclamationTriangle size={48} className="text-warning mb-3" />
        <p className="text-muted">No dashboard info available.</p>
      </div>
    );
  }

  const { plan, subscriptionPlan, apiKey, monthlyApiLimit, apiUsed } =
    dashboardInfo;
  const safeApiUsed = apiUsed || 0;
  const safeMonthlyApiLimit =
    monthlyApiLimit || (subscriptionPlan === "none" ? 0 : 1);
  const apiPercent =
    safeMonthlyApiLimit > 0
      ? Math.round((safeApiUsed / safeMonthlyApiLimit) * 100)
      : 0;
  const userName = dashboardInfo.userName || "User";

  const getProgressBarVariant = (percent) => {
    if (percent >= 90) return "danger";
    if (percent >= 75) return "warning";
    if (percent >= 50) return "info";
    return "success";
  };

  const getPlanIcon = (planName) => {
    if (planName?.toLowerCase().includes("ultimate")) return FaCrown;
    if (planName?.toLowerCase().includes("pro")) return FaGem;
    if (planName?.toLowerCase().includes("essential")) return FaRocket;
    if (planName?.toLowerCase().includes("basic")) return FaKey;
    return FaRocket;
  };

  // Helper function to determine if subscription is active
  const isSubscriptionActive = (planName, subscriptionPlan) => {
    return (
      subscriptionPlan &&
      subscriptionPlan !== "none" &&
      subscriptionPlan !== "FREE"
    );
  };

  // Helper function to get subscription status
  const getSubscriptionStatus = (planName, subscriptionPlan) => {
    if (!subscriptionPlan || subscriptionPlan === "none") {
      return {
        text: "No Active Plan",
        variant: "secondary",
        icon: FaExclamationTriangle,
      };
    }
    if (subscriptionPlan === "FREE") {
      return { text: "Free Plan", variant: "info", icon: FaCheckCircle };
    }
    return { text: "Active Plan", variant: "success", icon: FaCheckCircle };
  };

  const PlanIcon = getPlanIcon(plan);
  const subscriptionStatus = getSubscriptionStatus(plan, subscriptionPlan);

  const getStatusBadgeVariant = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "success";
    if (statusCode >= 400 && statusCode < 500) return "warning";
    if (statusCode >= 500) return "danger";
    return "secondary";
  };

  const formatApiLimit = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "number") return value.toLocaleString();
    return "N/A";
  };

  const handleCopyApiKey = async () => {
    const key = apiKey || "";
    if (!key) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(key);
      } else {
        // Fallback for older browsers
        const el = document.createElement("textarea");
        el.value = key;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
    } catch (e) {
      // no-op: avoid breaking UI if copy fails
      console.error("Failed to copy API key:", e);
    }
  };

  return (
    <div className="home-tab p-3">
      {/* Header Section */}
      <div className="d-flex justify-content-center mb-4">
        <Card style={{ width: "100%" }} className="shadow-sm">
          <Card.Header className="bg-primary text-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 fw-bold">
                <FaChartLine className="me-2" />
                Dashboard Overview
              </h4>
              <div className="text-end">
                <small className="d-block">Welcome back, {userName}!</small>
                <Badge bg="light" text="dark" className="px-2 py-1">
                  <FaCalendarAlt className="me-1" />
                  {new Date().toLocaleDateString()}
                </Badge>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="text-center py-2">
            <Alert
              variant="info"
              className="mb-0 d-flex align-items-center justify-content-center"
            >
              <FaCheckCircle className="me-2" />
              <span className="small">
                <strong>Live Data:</strong> Your API usage and request logs are
                now tracked in real-time.
              </span>
            </Alert>
          </Card.Body>
        </Card>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4 g-4">
        <Col lg={4} md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-info text-white text-center py-3">
              <FaRocket size={24} className="mb-2" />
              <h6 className="mb-0 fw-bold">API Usage - Current Period</h6>
            </Card.Header>
            <Card.Body className="text-center">
              <div className="mb-3">
                <div className="display-6 fw-bold text-primary mb-2">
                  {safeApiUsed.toLocaleString()}
                  <span className="fs-6 text-muted">
                    /
                    {safeMonthlyApiLimit === 0
                      ? "0"
                      : safeMonthlyApiLimit.toLocaleString()}
                  </span>
                </div>
                <ProgressBar
                  now={apiPercent}
                  variant={getProgressBarVariant(apiPercent)}
                  className="mb-2"
                  style={{ height: 12 }}
                  striped
                  animated={apiPercent > 0}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Badge
                    bg={getProgressBarVariant(apiPercent)}
                    className="px-2 py-1"
                  >
                    {apiPercent}% Used
                  </Badge>
                  <small className="text-muted">
                    {safeMonthlyApiLimit === 0
                      ? "0"
                      : (
                          safeMonthlyApiLimit - safeApiUsed
                        ).toLocaleString()}{" "}
                    remaining
                  </small>
                </div>
              </div>
              {/* Show different alerts based on plan status */}
              {subscriptionPlan === "none" && (
                <Alert variant="warning" className="small mb-0">
                  <FaExclamationTriangle className="me-1" />
                  No active subscription. Subscribe to start using the API!
                </Alert>
              )}
              {subscriptionPlan === "FREE" && apiPercent >= 80 && (
                <Alert variant="warning" className="small mb-0">
                  <FaExclamationTriangle className="me-1" />
                  Free plan limit almost reached. Consider upgrading!
                </Alert>
              )}
              {subscriptionPlan !== "none" &&
                subscriptionPlan !== "FREE" &&
                apiPercent >= 80 && (
                  <Alert variant="warning" className="small mb-0">
                    <FaExclamationTriangle className="me-1" />
                    Consider upgrading your plan soon!
                  </Alert>
                )}
              {/* Show Upgrade Plan button when API usage is 100% */}
              {apiPercent >= 100 && (
                <div className="mt-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="fw-bold w-100"
                    onClick={() => navigate('/dashboard/subscription')}
                    style={{
                      background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                      border: 'none',
                      boxShadow: '0 4px 15px rgba(13, 110, 253, 0.4)',
                      transition: 'all 0.3s ease',
                      padding: '10px 20px',
                      fontSize: '14px',
                      letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(13, 110, 253, 0.6)';
                      e.target.style.background = 'linear-gradient(135deg, #0b5ed7 0%, #084298 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(13, 110, 253, 0.4)';
                      e.target.style.background = 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)';
                    }}
                  >
                    <FaArrowUp className="me-2" />
                    Upgrade Plan
                  </Button>
                </div>
              )}

              <div className="mt-3">
                {subscriptionPlan === "FREE" && (
                  <div className="small text-muted mb-2">
                    You are on Free Plan – Start making API calls
                  </div>
                )}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="fw-bold w-100"
                  onClick={() => window.open("/documentation", "_blank")}
                >
                  Try Sample Request
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header
              className={`text-white text-center py-3 ${
                subscriptionStatus.variant === "success"
                  ? "bg-success"
                  : subscriptionStatus.variant === "info"
                  ? "bg-info"
                  : "bg-secondary"
              }`}
            >
              <PlanIcon size={24} className="mb-2" />
              <h6 className="mb-0 fw-bold">Subscription Status</h6>
            </Card.Header>
            <Card.Body className="text-center d-flex flex-column">
              <div className="flex-grow-1">
                <div className="display-6 fw-bold text-primary mb-2">
                  {plan || "No Plan"}
                </div>
                <Badge
                  bg={subscriptionStatus.variant}
                  className="mb-3 px-3 py-2"
                >
                  <subscriptionStatus.icon className="me-1" />
                  {subscriptionStatus.text}
                </Badge>
                {/* Dynamic description based on plan */}
                {subscriptionPlan === "none" && (
                  <>
                    <div className="text-muted small mb-3">
                      Subscribe to access our API services
                    </div>
                    <Button
                      variant="primary"
                      size="lg"
                      className="mt-2 fw-bold w-100"
                      onClick={() => navigate('/dashboard/subscription')}
                      style={{
                        background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                        border: 'none',
                        boxShadow: '0 4px 15px rgba(13, 110, 253, 0.4)',
                        transition: 'all 0.3s ease',
                        padding: '10px 20px',
                        fontSize: '14px',
                        letterSpacing: '0.5px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(13, 110, 253, 0.6)';
                        e.target.style.background = 'linear-gradient(135deg, #0b5ed7 0%, #084298 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(13, 110, 253, 0.4)';
                        e.target.style.background = 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)';
                      }}
                    >
                      <FaRocket className="me-2" />
                      Subscribe Now
                    </Button>
                  </>
                )}
                {subscriptionPlan === "FREE" && (
                  <div className="text-muted small mb-3">
                    Limited features available
                  </div>
                )}
                {isSubscriptionActive(plan, subscriptionPlan) && (
                  <div className="text-success small mb-3">
                    Full access to subscribed features
                  </div>
                )}
              </div>

              <div className="mt-2">
                <Button
                  variant="success"
                  size="sm"
                  className="fw-bold w-100"
                  onClick={() => navigate("/dashboard/subscription")}
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    border: "none",
                    boxShadow: "0 4px 15px rgba(16, 185, 129, 0.25)",
                  }}
                >
                  Find The Right Plan For You
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} md={12}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-secondary text-white text-center py-3">
              <FaKey size={24} className="mb-2" />
              <h6 className="mb-0 fw-bold">Your API Key</h6>
            </Card.Header>
            <Card.Body className="text-center d-flex flex-column">
              <div className="flex-grow-1">
                <div className="bg-light p-3 rounded mb-3">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <code
                      className="text-dark small word-break text-start flex-grow-1"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {apiKey || "No API Key"}
                    </code>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCopyApiKey}
                      disabled={!apiKey}
                      title="Copy API Key"
                      style={{ lineHeight: 1 }}
                    >
                      <FaCopy />
                    </Button>
                  </div>
                </div>
                <Badge bg="secondary" className="px-2 py-1 mb-3">
                  <FaCheckCircle className="me-1" />
                  Secure Key
                </Badge>
                <div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => window.open("/documentation", "_blank")}
                    className="w-100 fw-bold"
                    style={{
                      background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                      border: 'none',
                      boxShadow: '0 4px 15px rgba(13, 110, 253, 0.4)',
                      transition: 'all 0.3s ease',
                      padding: '10px 20px',
                      fontSize: '14px',
                      letterSpacing: '0.5px',
                      marginTop: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(13, 110, 253, 0.6)';
                      e.target.style.background = 'linear-gradient(135deg, #0b5ed7 0%, #084298 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(13, 110, 253, 0.4)';
                      e.target.style.background = 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)';
                    }}
                  >
                    <FaBook className="me-2" />
                    View API Docs
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="lg"
                    onClick={() => window.open("/api-playground", "_blank")}
                    className="w-100 fw-bold"
                    style={{
                      borderColor: "#0E38B1",
                      color: "#0E38B1",
                      marginTop: "10px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      letterSpacing: "0.5px",
                    }}
                  >
                    API Playground
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Chart Section */}
      <div className="d-flex justify-content-center">
        <Card style={{ width: "100%" }} className="shadow-sm">
          <Card.Header className="bg-primary text-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">
                <FaChartLine className="me-2" />
                API Usage Analytics
              </h5>
              <Badge bg="light" text="dark" className="px-2 py-1">
                Last 30 Days
              </Badge>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <div className="text-center mb-3">
              <p className="text-muted small mb-0">
                Daily API request patterns for the last 30 days. Real-time data
                from your actual usage.
              </p>
            </div>
            <div style={{ height: "400px", position: "relative" }}>
              {apiAnalytics && apiAnalytics.length > 0 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center">
                    <FaChartLine size={64} className="text-muted mb-3" />
                    <p className="text-muted">
                      No data available for chart. Start using the API to see
                      analytics.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card.Body>
          <Card.Footer className="bg-light text-center">
            <small className="text-muted">
              <FaChartLine className="me-1" />
              Real-time data updates automatically |
              <FaCalendarAlt className="ms-2 me-1" />
              Based on your actual API usage
            </small>
          </Card.Footer>
        </Card>
      </div>
      
      {/* Latest API Requests Table */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-warning text-dark py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">
                  <FaHistory className="me-2" />
                  Latest API Requests (30)
                </h5>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => {
                    dispatch(getLatestApiLogs());
                    dispatch(getApiUsageAnalytics());
                  }}
                  disabled={apiLogsLoading}
                >
                  <FaSync
                    className={`me-1 ${apiLogsLoading ? "fa-spin" : ""}`}
                  />
                  Refresh
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {apiLogsLoading ? (
                <div className="text-center py-4">
                  <Spinner />
                  <p className="mt-2 text-muted">Loading API logs...</p>
                </div>
              ) : apiLogs && apiLogs.length > 0 ? (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table responsive striped hover className="mb-0">
                    <thead className="bg-light sticky-top">
                      <tr>
                        <th>Endpoint</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Response Time</th>
                        <th>API Limit Left</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiLogs.map((log) => (
                        <tr key={log?.id || Math.random()}>
                          <td>
                            <code className="small">
                              {log?.endpoint || "Unknown"}
                            </code>
                          </td>
                          <td>
                            <Badge
                              bg={
                                log?.method === "GET"
                                  ? "primary"
                                  : log?.method === "POST"
                                  ? "success"
                                  : "secondary"
                              }
                              className="fw-bold"
                            >
                              {log?.method || "N/A"}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getStatusBadgeVariant(log?.statusCode)}>
                              {log?.statusCode || "N/A"}
                            </Badge>
                          </td>
                          <td>
                            <span
                              className={
                                log?.responseTime > 1000
                                  ? "text-warning fw-bold"
                                  : "text-success"
                              }
                            >
                              {log?.responseTime || 0}ms
                            </span>
                          </td>
                          <td>
                            <span className="fw-bold">
                              {formatApiLimit(log?.apiLimitAfter)}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatTime(log?.timestamp)}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FaHistory size={48} className="text-muted mb-3" />
                  <p className="text-muted">
                    No API requests found. Start using the API to see your
                    request history.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HomeTab;
